import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../../lib/apiClient';

/**
 * Shape returned by `POST /api/report-narrative`.
 *
 * Designed to render at the very top of Daily / Weekly / Monthly reports
 * as an AI-generated executive summary aimed at non-technical readers
 * (CISO, CFO, board). The card degrades gracefully if Azure OpenAI is
 * unconfigured or the breaker is open — it simply hides itself.
 */
export interface ExecutiveNarrative {
  verdict: 'HEALTHY' | 'NEEDS_ATTENTION' | 'URGENT';
  headline: string;
  summary: string;
  whatChanged: string;
  wins: string[];
  concerns: string[];
}

interface ApiResponse {
  period: string;
  narrative: ExecutiveNarrative;
  generatedAt: string;
}

type Period = 'daily' | 'weekly' | 'monthly';

interface Props {
  /** Which report the snapshot represents — drives the AI's framing. */
  period: Period;
  /** The report payload as returned by /api/{daily,weekly,monthly}-report.
   *  Pass `null` while the report itself is still loading. */
  snapshot: unknown;
  /** Cache buster — bump (e.g. by passing the report's generatedAt) to force
   *  a re-fetch when the user picks a different date / customer / month. */
  cacheKey: string;
}

const VERDICT_THEME: Record<
  ExecutiveNarrative['verdict'],
  { label: string; bg: string; border: string; pillBg: string; pillFg: string }
> = {
  HEALTHY: {
    label: 'HEALTHY',
    bg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    border: '#86efac',
    pillBg: '#16a34a',
    pillFg: '#ffffff',
  },
  NEEDS_ATTENTION: {
    label: 'NEEDS ATTENTION',
    bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    border: '#fcd34d',
    pillBg: '#d97706',
    pillFg: '#ffffff',
  },
  URGENT: {
    label: 'URGENT',
    bg: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
    border: '#fca5a5',
    pillBg: '#dc2626',
    pillFg: '#ffffff',
  },
};

export function ExecutiveSummary({ period, snapshot, cacheKey }: Props) {
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; data: ExecutiveNarrative }
    | { kind: 'unavailable'; reason: string }
  >({ kind: 'idle' });

  useEffect(() => {
    if (snapshot == null) { setState({ kind: 'idle' }); return; }
    const ctrl = new AbortController();
    let cancelled = false;
    setState({ kind: 'loading' });
    apiFetch<ApiResponse>('/report-narrative', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ period, snapshot }),
      signal: ctrl.signal,
    })
      .then((r) => { if (!cancelled) setState({ kind: 'ready', data: r.narrative }); })
      .catch((e: ApiError | Error) => {
        if (cancelled) return;
        // 401 is handled globally by the auth context — stay idle.
        if (e instanceof ApiError && e.notAuthenticated) { setState({ kind: 'idle' }); return; }
        // Truly hide the card when the AI is simply not configured or in
        // breaker cool-down — the report below is still complete on its own
        // and we don't want to nag the user with a banner about a feature
        // they may not have set up.
        if (e instanceof ApiError && (
          (e.body['error'] === 'not_configured') || e.circuitOpen
        )) {
          setState({ kind: 'idle' });
          return;
        }
        const reason = e instanceof Error ? e.message : String(e);
        setState({ kind: 'unavailable', reason });
      });
    // Abort the inflight fetch (and skip the state write) when inputs change
    // — this prevents racing fetches from rapid date/customer switches AND
    // cuts off the *paid* upstream call as soon as the user navigates away.
    return () => { cancelled = true; ctrl.abort(); };
  }, [period, snapshot, cacheKey]);

  if (state.kind === 'idle') return null;

  if (state.kind === 'loading') {
    return (
      <div
        style={{
          background: '#f8fafc',
          border: '1px dashed #cbd5e1',
          borderRadius: 12,
          padding: '20px 24px',
          marginBottom: 20,
          color: '#475569',
          fontSize: '0.9em',
        }}
        aria-busy="true"
        aria-live="polite"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Spinner />
          <strong style={{ color: '#0f172a' }}>Generating executive summary…</strong>
        </div>
        <div style={{ fontSize: '0.85em' }}>
          The AI is reading this report's KPIs and writing a board-ready summary.
          This usually takes 5–15 seconds.
        </div>
      </div>
    );
  }

  if (state.kind === 'unavailable') {
    return (
      <div
        style={{
          background: '#fff7ed',
          border: '1px solid #fed7aa',
          borderRadius: 12,
          padding: '12px 18px',
          marginBottom: 20,
          color: '#9a3412',
          fontSize: '0.85em',
        }}
      >
        <strong>Executive summary unavailable.</strong>{' '}
        <span style={{ color: '#7c2d12' }}>{state.reason}</span>{' '}
        The full report below is unaffected.
      </div>
    );
  }

  const n = state.data;
  const theme = VERDICT_THEME[n.verdict];
  const paragraphs = (n.summary ?? '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

  return (
    <div
      style={{
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        borderRadius: 14,
        padding: '20px 24px 18px',
        marginBottom: 22,
        boxShadow: '0 4px 12px rgba(15, 23, 42, 0.05)',
      }}
    >
      {/* Header row: verdict pill + AI badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              display: 'inline-block',
              padding: '4px 12px',
              borderRadius: 999,
              background: theme.pillBg,
              color: theme.pillFg,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 1,
            }}
          >
            {theme.label}
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Executive Summary
          </span>
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 10,
            color: '#64748b',
            background: 'rgba(255,255,255,0.6)',
            padding: '2px 8px',
            borderRadius: 999,
          }}
          title="This summary was generated by AI from the report's KPIs. Always validate critical decisions against the underlying data below."
        >
          <SparkleIcon /> AI-generated
        </span>
      </div>

      {/* Headline */}
      {n.headline && (
        <h2 style={{ margin: '4px 0 12px', color: '#0f172a', fontSize: '1.15em', fontWeight: 700, lineHeight: 1.35 }}>
          {n.headline}
        </h2>
      )}

      {/* Summary paragraphs */}
      <div style={{ color: '#1e293b', fontSize: '0.95em', lineHeight: 1.55 }}>
        {paragraphs.map((p, i) => (
          <p key={i} style={{ margin: i === 0 ? '0 0 10px' : '10px 0' }}>{p}</p>
        ))}
      </div>

      {/* What changed callout */}
      {n.whatChanged && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            background: 'rgba(255,255,255,0.65)',
            border: '1px solid rgba(15,23,42,0.08)',
            borderRadius: 8,
            fontSize: '0.88em',
            color: '#0f172a',
          }}
        >
          <strong style={{ color: '#0369a1' }}>What changed: </strong>
          <span>{n.whatChanged}</span>
        </div>
      )}

      {/* Wins + Concerns chips */}
      {(n.wins.length > 0 || n.concerns.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginTop: 14 }}>
          {n.wins.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#047857', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                Wins
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, color: '#065f46', fontSize: '0.85em', lineHeight: 1.45 }}>
                {n.wins.map((w, i) => <li key={i} style={{ marginBottom: 4 }}>{w}</li>)}
              </ul>
            </div>
          )}
          {n.concerns.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                Concerns
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, color: '#7f1d1d', fontSize: '0.85em', lineHeight: 1.45 }}>
                {n.concerns.map((c, i) => <li key={i} style={{ marginBottom: 4 }}>{c}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 14,
        height: 14,
        border: '2px solid #cbd5e1',
        borderTopColor: '#0f172a',
        borderRadius: '50%',
        animation: 'es-spin 0.8s linear infinite',
      }}
    >
      <style>{`@keyframes es-spin { to { transform: rotate(360deg); } }`}</style>
    </span>
  );
}

function SparkleIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 1l1.5 4.5L14 7l-4.5 1.5L8 13l-1.5-4.5L2 7l4.5-1.5L8 1z" />
    </svg>
  );
}
